// 4-step questionnaire wizard.
//
// Steps (in order, mirrors how the SPA describes the assessment to
// the user):
//   1. Risk Tolerance        (10 RTQ questions)
//   2. Financial Capacity    (5  FC questions)
//   3. Behavioral Insights   (20 behavioral questions)
//   4. Investment Style      (8  style 1-5 likert questions)
//
// One question per card. The wizard keeps a sticky progress bar at
// the top so the user always sees how far along they are. Auto-save
// runs in the background via ``useAssessmentForm`` and surfaces a
// "Saved" / "Saving…" / "Save failed" badge next to the bar.
//
// On the last step the primary CTA flips from "Continue" to
// "Calculate Profile" and triggers ``onCalculate``. The parent route
// (RiskAssessmentFlow) is responsible for the actual API call so the
// wizard stays oblivious to results-page navigation.

import { useMemo, useState } from 'react';

import { Button } from '../../../shared/ui';
import type {
    QuestionnairesResponse,
    RawAnswers,
    ScaleQuestion,
    StyleQuestion,
} from '../api/riskApi';
import type { SaveStatus } from '../hooks/useAssessmentForm';
import { QuestionCard, type QuestionOption } from './QuestionCard';

// ---------------------------------------------------------------------------
// Step model -- the wizard never special-cases a section in render; it
// just iterates ``WIZARD_STEPS`` and looks up questions on the fly.
// ---------------------------------------------------------------------------

interface WizardStep {
    key: 'tolerance' | 'capacity' | 'behavioral' | 'style';
    title: string;
    description: string;
    answersKey: keyof RawAnswers;
}

const WIZARD_STEPS: WizardStep[] = [
    {
        key: 'tolerance',
        title: 'Risk Tolerance',
        description:
            'Ten scenarios that reveal how you respond to drawdowns, time horizons and uncertainty.',
        answersKey: 'rtq_answers',
    },
    {
        key: 'capacity',
        title: 'Financial Capacity',
        description:
            'Five questions about income, savings rate and debt — the *ability* to take risk, separate from your *willingness*.',
        answersKey: 'fc_answers',
    },
    {
        key: 'behavioral',
        title: 'Behavioral Insights',
        description:
            'Twenty likert prompts mapped to the cognitive biases that quietly drive most investment mistakes.',
        answersKey: 'behavioral_answers',
    },
    {
        key: 'style',
        title: 'Investment Style',
        description:
            'Eight statements that calibrate where you sit on the active / passive spectrum.',
        answersKey: 'style_answers',
    },
];

// Style questionnaire is a bare 1-5 likert; rendered with the same
// labels as the platform's marketing copy.
const STYLE_LIKERT: QuestionOption[] = [
    { value: 1, label: 'Strongly disagree' },
    { value: 2, label: 'Disagree' },
    { value: 3, label: 'Neutral' },
    { value: 4, label: 'Agree' },
    { value: 5, label: 'Strongly agree' },
];

// ---------------------------------------------------------------------------
// Question normalisation -- collapse the four input shapes onto a
// uniform { id, prompt, options, category? } record so QuestionCard
// has a single render path. Memoised by the caller.
// ---------------------------------------------------------------------------

interface NormalisedQuestion {
    id: string;
    prompt: string;
    options: QuestionOption[];
    section: WizardStep;
    indexInSection: number;
    sectionSize: number;
    category?: string;
}

function normaliseRtq(qs: QuestionnairesResponse['rtq']): NormalisedQuestion[] {
    const step = WIZARD_STEPS[0];
    return qs.questions.map((q, i) => ({
        id: String(q.id),
        prompt: q.question,
        // RTQ answers is an object {A:..,B:..,..}; we sort by key so
        // A appears above B regardless of insertion order.
        options: Object.entries(q.answers)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([letter, label]) => ({ value: letter, label })),
        section: step,
        indexInSection: i,
        sectionSize: qs.questions.length,
    }));
}

function normaliseScale(
    questions: Record<string, ScaleQuestion>,
    step: WizardStep,
): NormalisedQuestion[] {
    // Sort keys naturally so Q2 < Q10 instead of lexicographically.
    const keys = Object.keys(questions).sort((a, b) => {
        const an = parseInt(a.replace(/\D/g, ''), 10);
        const bn = parseInt(b.replace(/\D/g, ''), 10);
        return an - bn;
    });
    return keys.map((id, i) => {
        const q = questions[id];
        return {
            id,
            prompt: q.text,
            // Scale keys are strings ("1".."5") on the wire but we
            // surface them as numbers so the answer typing is
            // consistent with the backend's integer expectation.
            options: Object.entries(q.scale)
                .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
                .map(([n, label]) => ({ value: parseInt(n, 10), label })),
            section: step,
            indexInSection: i,
            sectionSize: keys.length,
            category: q.category,
        };
    });
}

function normaliseStyle(qs: StyleQuestion[]): NormalisedQuestion[] {
    const step = WIZARD_STEPS[3];
    return qs.map((q, i) => ({
        id: q.id,
        prompt: q.text,
        options: STYLE_LIKERT,
        section: step,
        indexInSection: i,
        sectionSize: qs.length,
    }));
}

export function buildQuestionList(
    questionnaires: QuestionnairesResponse,
): NormalisedQuestion[] {
    return [
        ...normaliseRtq(questionnaires.rtq),
        ...normaliseScale(questionnaires.fc, WIZARD_STEPS[1]),
        ...normaliseScale(questionnaires.behavioral, WIZARD_STEPS[2]),
        ...normaliseStyle(questionnaires.style),
    ];
}

// ---------------------------------------------------------------------------
// Wizard component
// ---------------------------------------------------------------------------

export interface AssessmentWizardProps {
    questionnaires: QuestionnairesResponse;
    answers: RawAnswers;
    setAnswer: (
        section: keyof RawAnswers,
        questionId: string,
        value: string | number,
    ) => void;
    onCalculate: () => void;
    /** True while /calculate-full-profile is in flight. */
    calculating?: boolean;
    /** IDs returned by a 422 response so the wizard can flag them. */
    invalidIds?: string[];
    saveStatus: SaveStatus;
    /**
     * Fired whenever the cursor crosses a section boundary -- i.e. the
     * user clicks "Continue" on the last question of a section and is
     * about to land on the first question of the next one. The parent
     * uses this hook to flush the debounced /save-progress so the
     * server has the section's answers on file before the user moves
     * on. The promise is awaited but failures must NOT block the
     * transition (auto-save is best-effort UX).
     */
    onSectionAdvance?: (
        from: WizardStep['key'],
        to: WizardStep['key'],
    ) => Promise<void> | void;
}

const SaveBadge = ({ status }: { status: SaveStatus }) => {
    const map: Record<SaveStatus, { label: string; className: string }> = {
        idle: {
            label: 'Auto-saving',
            className: 'text-slate-500 dark:text-slate-400',
        },
        saving: {
            label: 'Saving…',
            className: 'text-amber-600 dark:text-amber-400',
        },
        saved: {
            label: 'Saved',
            className: 'text-emerald-600 dark:text-emerald-400',
        },
        error: {
            label: 'Save failed — will retry',
            className: 'text-red-600 dark:text-red-400',
        },
    };
    const { label, className } = map[status];
    return (
        <span className={`text-xs font-semibold ${className}`}>
            {label}
        </span>
    );
};

export const AssessmentWizard = ({
    questionnaires,
    answers,
    setAnswer,
    onCalculate,
    calculating = false,
    invalidIds = [],
    saveStatus,
    onSectionAdvance,
}: AssessmentWizardProps) => {
    const questions = useMemo(
        () => buildQuestionList(questionnaires),
        [questionnaires],
    );

    const [cursor, setCursor] = useState(0);
    const total = questions.length;
    const current = questions[cursor];
    const onLast = cursor === total - 1;

    // Per-section answered-count -- powers both the segmented progress
    // bar at the top and the section-completeness check on the chip
    // strip. Recomputed on every render: the math is O(n) over the
    // 43 question dictionary which is trivially cheap, and avoiding a
    // useMemo here keeps the answer dependency clean.
    const sectionStats = WIZARD_STEPS.map((step) => {
        const dict = answers[step.answersKey] as Record<
            string,
            string | number
        >;
        // Count only IDs that exist in the questionnaire payload (so a
        // stale draft on a JSON file that was renamed doesn't inflate
        // the bar). The size comes straight from the normalised list.
        const sectionQuestions = questions.filter(
            (q) => q.section.key === step.key,
        );
        const size = sectionQuestions.length;
        const answered = sectionQuestions.reduce((acc, q) => {
            const v = dict?.[q.id];
            return v === undefined || v === '' ? acc : acc + 1;
        }, 0);
        return { step, answered, size };
    });

    const invalidSet = useMemo(() => new Set(invalidIds), [invalidIds]);

    const currentValue = (() => {
        const dict = answers[current.section.answersKey] as Record<
            string,
            string | number
        >;
        return dict?.[current.id];
    })();

    const isAnsweredHere = currentValue !== undefined && currentValue !== '';

    // Per-section progress -- "Q3 of 20" inside Behavioral, etc. Lets
    // the user gauge how much is left even when they're deep in a
    // long section.
    const stepStartIdx = useMemo(() => {
        const idx: Record<WizardStep['key'], number> = {
            tolerance: -1,
            capacity: -1,
            behavioral: -1,
            style: -1,
        };
        for (let i = 0; i < questions.length; i += 1) {
            const k = questions[i].section.key;
            if (idx[k] === -1) idx[k] = i;
        }
        return idx;
    }, [questions]);

    const handleBack = () => {
        setCursor((c) => Math.max(0, c - 1));
    };
    const handleForward = async () => {
        if (onLast) {
            onCalculate();
            return;
        }
        const next = questions[cursor + 1];
        // Section boundary? Flush /save-progress before advancing so
        // the backend has the just-completed section on file even if
        // the user closes the tab on the very next prompt. We await
        // the parent's hook so a slow flush keeps the user on the
        // current question (with a "Saving…" badge) instead of
        // disappearing into the next section -- but errors are
        // swallowed because the in-memory state is still valid.
        if (next.section.key !== current.section.key && onSectionAdvance) {
            try {
                await onSectionAdvance(current.section.key, next.section.key);
            } catch {
                // No-op: surfaced via the SaveBadge already.
            }
        }
        setCursor((c) => Math.min(total - 1, c + 1));
    };

    // Jump-to-section affordance in the progress bar -- clicking the
    // chip for "Behavioral Insights" hops the cursor to its first
    // unanswered question (or the section's first question if every
    // answer is filled).
    const jumpToSection = (step: WizardStep) => {
        const start = stepStartIdx[step.key];
        if (start === -1) return;
        const dict = answers[step.answersKey] as Record<string, string | number>;
        let target = start;
        for (let i = start; i < questions.length; i += 1) {
            if (questions[i].section.key !== step.key) break;
            const v = dict?.[questions[i].id];
            if (v === undefined || v === '') {
                target = i;
                break;
            }
            target = i;
        }
        setCursor(target);
    };

    return (
        <div className="space-y-6">
            {/* Sticky progress header -- pinned below the page header
                so the user can see their progress while scrolling
                through an option list on small screens. */}
            <div className="sticky top-16 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 bg-slate-200/85 dark:bg-slate-900/85 backdrop-blur border-b border-slate-300 dark:border-slate-800">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        {current.section.title} · Question{' '}
                        {current.indexInSection + 1} of {current.sectionSize}
                    </span>
                    <SaveBadge status={saveStatus} />
                    <span className="ml-auto text-xs muted">
                        {Math.round(((cursor + 1) / total) * 100)}% complete
                    </span>
                </div>

                {/* Segmented progress bar -- one fill per section so the
                    user always sees the four pillars at a glance. The
                    active section is bordered + brightened; completed
                    sections show a solid emerald fill. Clicking a
                    segment jumps the cursor to its first unanswered
                    question (delegating to ``jumpToSection``). */}
                <ol
                    className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4"
                    aria-label="Assessment progress by section"
                >
                    {sectionStats.map(({ step, answered, size }) => {
                        const active = current.section.key === step.key;
                        const pct = size === 0 ? 0 : (answered / size) * 100;
                        const complete = answered === size && size > 0;
                        return (
                            <li key={step.key}>
                                <button
                                    type="button"
                                    onClick={() => jumpToSection(step)}
                                    aria-current={active ? 'step' : undefined}
                                    aria-label={`${step.title}: ${answered} of ${size} answered`}
                                    className={[
                                        'group block w-full rounded-xl border px-3 py-2 text-left transition-colors',
                                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400',
                                        active
                                            ? 'border-emerald-500 bg-white dark:bg-slate-900 shadow-sm'
                                            : complete
                                              ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'
                                              : 'border-slate-300 dark:border-slate-700 bg-white/60 dark:bg-slate-900/40 hover:border-emerald-400',
                                    ].join(' ')}
                                >
                                    <div className="flex items-baseline justify-between gap-2">
                                        <span
                                            className={[
                                                'text-[11px] font-bold uppercase tracking-wider',
                                                active
                                                    ? 'text-emerald-700 dark:text-emerald-300'
                                                    : complete
                                                      ? 'text-emerald-700 dark:text-emerald-300'
                                                      : 'text-slate-600 dark:text-slate-300',
                                            ].join(' ')}
                                        >
                                            {step.title}
                                        </span>
                                        <span className="text-[10px] font-semibold tabular-nums text-slate-500 dark:text-slate-400">
                                            {answered}/{size}
                                        </span>
                                    </div>
                                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                                        <div
                                            className={[
                                                'h-full rounded-full transition-[width] duration-500',
                                                complete
                                                    ? 'bg-emerald-500'
                                                    : active
                                                      ? 'bg-gradient-to-r from-green-500 to-emerald-300'
                                                      : 'bg-emerald-400/70',
                                            ].join(' ')}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ol>
            </div>

            {/* Section description -- only on the FIRST question of a
                section so it doesn't bury the prompt on subsequent
                cards. */}
            {current.indexInSection === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 px-5 py-4 bg-slate-50 dark:bg-slate-900/40">
                    <h2 className="section-title">{current.section.title}</h2>
                    <p className="mt-1 text-sm muted">
                        {current.section.description}
                    </p>
                </div>
            )}

            {/* The question itself. */}
            <QuestionCard
                indexLabel={`Question ${current.indexInSection + 1} of ${current.sectionSize}`}
                sectionLabel={current.section.title}
                categoryLabel={current.category}
                prompt={current.prompt}
                options={current.options}
                value={currentValue}
                onChange={(v) =>
                    setAnswer(current.section.answersKey, current.id, v)
                }
                invalid={invalidSet.has(current.id)}
            />

            {/* Footer nav. The Continue button is enabled on every step
                so the user can skip and come back -- the backend
                completeness check will catch missing answers on
                Calculate. */}
            <div className="flex items-center justify-between gap-3">
                <Button
                    variant="secondary"
                    size="md"
                    onClick={handleBack}
                    disabled={cursor === 0 || calculating}
                >
                    Back
                </Button>
                <div className="flex-1 text-center text-xs muted">
                    {isAnsweredHere
                        ? 'Answer saved — continue when ready.'
                        : 'Choose an option to continue.'}
                </div>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleForward}
                    disabled={calculating}
                >
                    {onLast
                        ? calculating
                            ? 'Calculating…'
                            : 'Calculate Profile'
                        : 'Continue'}
                </Button>
            </div>
        </div>
    );
};

export default AssessmentWizard;
